import { useEffect, useRef, useState } from 'react';
import type { VerificationStatus } from '../../hooks/usePhoneVerification';
import { trackEvent } from '../../utils/track';

interface Customer {
  name: string;
  email: string;
  phone: string;
  cpf: string;
}

interface Verification {
  status: VerificationStatus;
  error: string | null;
  secondsLeft: number;
  start: (phone: string, name: string) => Promise<void>;
  confirm: (phone: string, code: string) => Promise<boolean>;
  reset: () => void;
}

interface Props {
  value: Customer;
  verification: Verification;
  experienceCode: string;
  bookingType: string;
  onChange: (v: Customer) => void;
  onNext: () => void;
}

function maskPhone(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4}).*/, '($1) $2-$3').trim();
  return d.replace(/(\d{2})(\d{5})(\d{0,4}).*/, '($1) $2-$3').trim();
}

function maskCpf(v: string) {
  return v
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d{3})(\d{3})(\d{0,2}).*/, '$1.$2.$3-$4')
    .replace(/[-.]$/, '');
}

function isValidCpf(input: string): boolean {
  const d = input.replace(/\D/g, '');
  if (d.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(d)) return false;
  const digits = d.split('').map(Number);
  const check = (end: number) => {
    let sum = 0;
    for (let i = 0; i < end; i++) sum += digits[i] * (end + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return check(9) === digits[9] && check(10) === digits[10];
}

function toE164BR(masked: string): string {
  const d = masked.replace(/\D/g, '');
  return d ? `55${d}` : '';
}

export function CustomerStep({ value, verification, experienceCode, bookingType, onChange, onNext }: Props) {
  const set = <K extends keyof Customer>(k: K, v: Customer[K]) =>
    onChange({ ...value, [k]: v });
  const [otpCode, setOtpCode] = useState('');
  const [otpVisible, setOtpVisible] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Funnel: chegada no step de dados pessoais. Roda 1x por mount.
  useEffect(() => {
    trackEvent('booking_customer_step_view', { experienceCode, bookingType });
  }, [experienceCode, bookingType]);

  const phoneDigits = value.phone.replace(/\D/g, '');
  const phoneFilled = phoneDigits.length >= 10;
  // Aceita nomes curtos válidos no PT-BR ("Ed", "Lu", "Ra"). Era >2 antes —
  // travava esses casos sem motivo. O nome só vira metadado da template WA.
  const nameFilled = value.name.trim().length >= 2;
  const phoneVerified = verification.status === 'verified';
  const cpfFilled = value.cpf.replace(/\D/g, '').length === 11;
  const cpfValid = isValidCpf(value.cpf);
  const emailValid = /^\S+@\S+\.\S+$/.test(value.email);

  const isValid = nameFilled && emailValid && phoneFilled && phoneVerified && cpfValid;

  // Reset verification + esconde slot OTP ao editar telefone.
  useEffect(() => {
    verification.reset();
    setOtpVisible(false);
    setOtpCode('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneDigits]);

  // Foca o OTP quando entra em awaitingCode (apos start bem-sucedido).
  useEffect(() => {
    if (verification.status === 'awaitingCode' && otpVisible) {
      const t = window.setTimeout(() => otpInputRef.current?.focus(), 80);
      return () => window.clearTimeout(t);
    }
  }, [verification.status, otpVisible]);

  // Auto-confirma quando o OTP atinge 5 digitos. Evita o atrito do "click
  // pra confirmar". UX igual ao login da Apple/Google.
  useEffect(() => {
    if (otpCode.length !== 5) return;
    if (verification.status !== 'awaitingCode') return;
    void verification.confirm(toE164BR(value.phone), otpCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode]);

  // Dispara phone_verification_completed no momento exato em que o status
  // transiciona pra 'verified'. useEffect garante 1x mesmo com retries.
  useEffect(() => {
    if (phoneVerified) {
      trackEvent('phone_verification_completed', { experienceCode, bookingType });
    }
  }, [phoneVerified, experienceCode, bookingType]);

  async function handleVerifyClick() {
    if (!phoneFilled || !nameFilled) return;
    setOtpVisible(true);
    setOtpCode('');
    trackEvent('phone_verification_started', { experienceCode, bookingType });
    if (verification.status === 'idle') {
      await verification.start(toE164BR(value.phone), value.name.trim());
    }
  }

  function handleResend() {
    setOtpCode('');
    verification.start(toE164BR(value.phone), value.name.trim());
  }

  // Helper text dinamico abaixo do CTA principal — explica EXATAMENTE o
  // que falta pra o botao habilitar (em vez de deixar o usuario adivinhando).
  const continueHint = (() => {
    if (isValid) return null;
    if (!nameFilled) return 'Preencha seu nome para continuar.';
    if (!phoneFilled) return 'Informe um WhatsApp válido para continuar.';
    if (!phoneVerified) return 'Verifique seu WhatsApp para continuar.';
    if (!emailValid) return 'Informe um e-mail válido para continuar.';
    if (!cpfValid) return 'Informe um CPF válido para continuar.';
    return null;
  })();

  return (
    <div className="step">
      <h2 className="step__title">Seus dados</h2>
      <p className="step__lead">Para emitir a reserva e enviar a confirmação.</p>

      <label className="field">
        <span className="field__label">Nome completo</span>
        <input
          className="field__input"
          value={value.name}
          onChange={(e) => set('name', e.target.value)}
          autoComplete="name"
        />
      </label>

      <label className="field">
        <span className="field__label">WhatsApp</span>
        <input
          className="field__input"
          value={value.phone}
          onChange={(e) => set('phone', maskPhone(e.target.value))}
          autoComplete="tel"
          inputMode="tel"
          placeholder="(11) 99999-9999"
        />
        {phoneVerified ? (
          <button type="button" className="btn--verify btn--verify--done" disabled>
            ✓ WhatsApp verificado
          </button>
        ) : (
          <button
            type="button"
            className="btn--verify"
            disabled={!phoneFilled || !nameFilled || verification.status === 'sending'}
            onClick={handleVerifyClick}
          >
            {verification.status === 'sending' ? 'Enviando código…' : 'Verificar'}
          </button>
        )}

        {/* Slot OTP inline — abre apos clicar Verificar, fica aberto durante
            todo o ciclo (sending → awaitingCode → verifying → verified).
            Esconde quando phoneVerified ou quando o usuario edita o telefone. */}
        {!phoneVerified && otpVisible && (
          <div className="otp-inline" role="group" aria-label="Verificação por WhatsApp">
            <p className="otp-inline__hint">
              Enviamos um código de 5 dígitos para <strong>{value.phone}</strong>.
            </p>
            <input
              ref={otpInputRef}
              className="otp-inline__input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={5}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="00000"
              aria-label="Código de verificação"
              disabled={verification.status === 'verifying' || verification.status === 'sending'}
            />
            <div className="otp-inline__foot">
              {verification.status === 'verifying' && (
                <span className="otp-inline__status">Verificando…</span>
              )}
              {verification.status === 'awaitingCode' && verification.secondsLeft > 0 && (
                <span className="otp-inline__status">
                  Reenviar em {verification.secondsLeft}s
                </span>
              )}
              {verification.status === 'awaitingCode' && verification.secondsLeft === 0 && (
                <button
                  type="button"
                  className="otp-inline__resend"
                  onClick={handleResend}
                >
                  Reenviar código
                </button>
              )}
            </div>
            {verification.error && (
              <small className="error">{verification.error}</small>
            )}
          </div>
        )}
      </label>

      <label className="field">
        <span className="field__label">E-mail</span>
        <input
          type="email"
          className="field__input"
          value={value.email}
          onChange={(e) => set('email', e.target.value)}
          autoComplete="email"
          inputMode="email"
          aria-invalid={value.email.length > 0 && !emailValid}
        />
      </label>

      <label className="field">
        <span className="field__label">CPF</span>
        <input
          className="field__input"
          value={value.cpf}
          onChange={(e) => set('cpf', maskCpf(e.target.value))}
          inputMode="numeric"
          placeholder="000.000.000-00"
          aria-invalid={cpfFilled && !cpfValid}
        />
        {cpfFilled && !cpfValid && (
          <small className="error">CPF inválido. Confira os dígitos.</small>
        )}
      </label>

      <div className="step__cta-row">
        <button
          type="button"
          className="btn btn--brand btn--lg btn--block"
          onClick={onNext}
          disabled={!isValid}
        >
          Continuar
        </button>
        {continueHint && (
          <p className="step__cta-hint" role="status">
            {continueHint}
          </p>
        )}
      </div>
    </div>
  );
}
